import {
  getIBPulseFeed,
  recordIBPulseEngagement,
  recordIBPulseView,
  setIBPulseDismissed,
  setIBPulseFilter,
  setIBPulseUpcomingCollapsed,
} from '@/api/ibModule';

// The server tracks dismissals per surface: the feed row (its default) and the
// upcoming-release announcement above it. Closing a card must not hide the
// release from the feed once it lands, so the card sends its own scope.
export const UPCOMING_SCOPE = 'upcoming';

function dedupeById(existing, incoming) {
  const seen = new Set(existing.map((event) => event.id));
  return (incoming || []).filter((event) => !seen.has(event.id));
}

// The loaded feed's cursors. `events` is strictly newest-first: every slice is
// ordered by `-created_at` server-side, "show previous" appends older rows at the
// tail and the live poll prepends newer ones at the head. Nothing is hoisted out
// of date order any more, so position implies age and the bounds are just the
// ends of the list.
const newestCreatedAt = (events) => events[0].created_at;
const oldestCreatedAt = (events) => events[events.length - 1].created_at;

// The advisor's filter selection, in the shape the feed endpoint reads. Every
// request carries it so each slice is filtered and sized server-side — the
// reason a chip-narrowed feed comes back full instead of being trimmed to
// whatever survives in one already-fetched page. ('Show all commodities' is not
// here: it is persisted per advisor, so the server reads it itself.)
function filterPayload(state) {
  return {
    ...(state.showDismissed ? { show_dismissed: true } : {}),
    ...(state.suppressedSources.length ? { exclude_sources: state.suppressedSources } : {}),
  };
}

// Filter changes re-fetch, so requests overlap: a chip toggled twice quickly, or
// a 30s poll still in flight when the book toggle flips. Each fetch takes the
// next token and drops its response if a newer one has since been issued, so the
// feed can never settle on a slice built for filters the advisor has moved on
// from. Module-scoped because nothing renders from it.
let latestFetchToken = 0;

// Whether a loaded row is on screen. The book, source and dismissed filters are
// all applied by the query that fetched it, so what is left here is the state
// this session holds ahead of the server: rows dismissed or blasted just now,
// which have to disappear on the click rather than on the next poll. A row the
// server itself flagged dismissed is held to the same rule, so one carried over
// from a wider fetch can't leak into the default view.
function isVisible(state) {
  return (event) => {
    const dismissed = event.dismissed || state.dismissedIds.includes(event.id);
    return (state.showDismissed || !dismissed) && !state.blastedIds.includes(event.id);
  };
}

export default {
  namespaced: true,

  state: () => ({
    // Lazy-loaded, newest-first. Preloaded with today's events (or the latest
    // few) and grown backwards by "Show previous events"; new events prepend on
    // the live poll. Accumulation lives here in Vuex, so it survives tab
    // switches and resets only on a full page reload (fresh store).
    events: [],
    freshHasMore: false,
    catchUp: { events: [], hasMore: false },
    // The announcement strip above the feed: scheduled releases still ahead of
    // us plus any that landed in the last day, and the advisor's persisted
    // minimize choice. Server-owned and small, so every feed load replaces it
    // rather than accumulating — that is also how a card learns it has landed.
    upcoming: { events: [], collapsed: false },
    // Cards closed in this session (AC7.2 semantics, announcement scope): hidden
    // immediately here, then persisted per advisor.
    dismissedUpcomingIds: [],
    loading: false,
    loaded: false,
    error: false,
    // Source segment chips: the sources currently suppressed (hidden), sent with
    // every feed request so the server filters and sizes each slice. Empty means
    // "show everything" — an advisor toggles a category off temporarily without
    // dismissing individual items. Stated as an exclusion so a source with no
    // chip (a new upstream type, the advisor's own notes) is never filtered out
    // with no way to bring it back. Session-only, like "Show dismissed".
    suppressedSources: [],
    // Optimistic dismissals for this session: a dismissed event is hidden
    // immediately here, then persisted per-advisor server-side (AC7.2) so it
    // stays gone across reloads. The server also excludes dismissed events from
    // the default feed, so these ids only matter until the next fetch.
    dismissedIds: [],
    // Session-only "Show dismissed" toggle (AC7.4). When on, feed fetches ask
    // the server to keep this advisor's dismissed events in the fresh slice
    // (each flagged with `dismissed`), so they can be reviewed and restored.
    // Not persisted — it resets to hidden on reload.
    showDismissed: false,
    // Events blasted in this session, so a send moves the item into the
    // catch-up table immediately; the next poll reconciles with the server,
    // which owns the durable split (IBBroadcast.pulse_event).
    blastedIds: [],
    // Book filtering (Story 1.4). The feed is scoped server-side to the
    // commodities in the advisor's book (union of their active clients' crops)
    // unless showAll is on. showAll is persisted per advisor server-side; the
    // feed response echoes both, so this mirrors the server's durable state.
    // bookTags drives the "filtering to N commodities" hint and empty-state copy.
    showAll: false,
    bookTags: [],
    // The source segment chips' options, driven by the distinct sources actually
    // present in the (book-scoped) data server-side — not a hardcoded list — so a
    // new upstream source type surfaces a chip with no frontend change.
    sources: [],
  }),

  getters: {
    // The fresh feed. Every filter (book scope, source chips, dismissed) is
    // already applied by the query that produced `events`, so all that is left
    // here is the optimistic state this session holds ahead of its next fetch:
    // rows dismissed or blasted just now, which must disappear immediately
    // rather than on the next poll.
    freshEvents(state) {
      return state.events.filter(isVisible(state));
    },

    // The catch-up table: events blasted this session (moved over locally,
    // newest send first) ahead of the server-provided rows.
    catchUpEvents(state) {
      const serverIds = new Set(state.catchUp.events.map((event) => event.id));
      const local = state.events.filter(
        (event) => state.blastedIds.includes(event.id) && !serverIds.has(event.id),
      );
      return [...local, ...state.catchUp.events];
    },

    isCatchUp: (state, getters) => (eventId) => (
      getters.catchUpEvents.some((event) => event.id === eventId)
    ),

    // The announcement cards on screen: server-visible ones minus anything
    // closed here, unless the "Show dismissed" view is on.
    upcomingEvents(state) {
      return state.upcoming.events
        .filter((event) => state.showDismissed || !event.dismissed)
        .filter((event) => state.showDismissed || !state.dismissedUpcomingIds.includes(event.id));
    },
  },

  mutations: {
    setLoading(state, value) {
      state.loading = Boolean(value);
    },
    setError(state, value) {
      state.error = Boolean(value);
    },
    // Server-owned book-filter state, echoed on every feed load and returned by
    // the toggle endpoint. Guarded so a slice response without a filters block
    // (older/newer head refreshes may omit it) doesn't clobber known state.
    setFilters(state, filters) {
      if (!filters) return;
      state.showAll = Boolean(filters.show_all);
      state.bookTags = filters.book || [];
      state.sources = filters.sources || [];
    },
    // Initial load / Refresh: replace the fresh feed from the top.
    setFresh(state, { events, has_more: hasMore } = {}) {
      state.events = events || [];
      state.freshHasMore = Boolean(hasMore);
      state.loaded = true;
      state.error = false;
    },
    // "Show previous events": older batch appended at the tail.
    appendFresh(state, { events, has_more: hasMore } = {}) {
      state.events = [...state.events, ...dedupeById(state.events, events)];
      state.freshHasMore = Boolean(hasMore);
    },
    // Live poll: newer events prepended at the head (tail untouched).
    prependFresh(state, { events } = {}) {
      state.events = [...dedupeById(state.events, events), ...state.events];
    },
    setCatchUp(state, { events, has_more: hasMore } = {}) {
      state.catchUp = { events: events || [], hasMore: Boolean(hasMore) };
    },
    appendCatchUp(state, { events, has_more: hasMore } = {}) {
      state.catchUp = {
        events: [...state.catchUp.events, ...dedupeById(state.catchUp.events, events)],
        hasMore: Boolean(hasMore),
      };
    },
    prependCatchUp(state, { events } = {}) {
      state.catchUp = {
        ...state.catchUp,
        events: [...dedupeById(state.catchUp.events, events), ...state.catchUp.events],
      };
    },
    // Server-owned announcement strip, replaced on every feed load — that is how
    // a card learns it has landed. It does NOT push landed releases into the
    // feed: a release announced days ago belongs at its own age in a
    // chronological feed, not at the head. The strip itself is where a just-landed
    // report is surfaced (it carries the release link for a day after it lands),
    // and the next full load places the row by date like any other event.
    setUpcoming(state, { events, collapsed } = {}) {
      state.upcoming = { events: events || [], collapsed: Boolean(collapsed) };
    },
    setUpcomingCollapsed(state, collapsed) {
      state.upcoming = { ...state.upcoming, collapsed: Boolean(collapsed) };
    },
    dismissUpcoming(state, eventId) {
      if (!state.dismissedUpcomingIds.includes(eventId)) {
        state.dismissedUpcomingIds = [...state.dismissedUpcomingIds, eventId];
      }
      state.upcoming = {
        ...state.upcoming,
        events: state.upcoming.events.map(
          (event) => (event.id === eventId ? { ...event, dismissed: true } : event),
        ),
      };
    },
    restoreUpcoming(state, eventId) {
      state.dismissedUpcomingIds = state.dismissedUpcomingIds.filter((id) => id !== eventId);
      state.upcoming = {
        ...state.upcoming,
        events: state.upcoming.events.map(
          (event) => (event.id === eventId ? { ...event, dismissed: false } : event),
        ),
      };
    },
    toggleSource(state, source) {
      if (state.suppressedSources.includes(source)) {
        state.suppressedSources = state.suppressedSources.filter((s) => s !== source);
      } else {
        state.suppressedSources = [...state.suppressedSources, source];
      }
    },
    dismissEvent(state, eventId) {
      if (!state.dismissedIds.includes(eventId)) {
        state.dismissedIds = [...state.dismissedIds, eventId];
      }
      // Flag the loaded row too, so it reads as dismissed (Restore affordance)
      // when it stays visible under the "Show dismissed" view.
      state.events = state.events.map(
        (event) => (event.id === eventId ? { ...event, dismissed: true } : event),
      );
    },
    restoreEvent(state, eventId) {
      state.dismissedIds = state.dismissedIds.filter((id) => id !== eventId);
      state.events = state.events.map(
        (event) => (event.id === eventId ? { ...event, dismissed: false } : event),
      );
    },
    setShowDismissed(state, value) {
      state.showDismissed = Boolean(value);
    },
    markBlasted(state, eventId) {
      if (!state.blastedIds.includes(eventId)) {
        state.blastedIds = [...state.blastedIds, eventId];
      }
    },
  },

  actions: {
    // Initial preload of both collections. Also the reload every filter change
    // runs through (chips, "show all commodities", "show dismissed"): the feed
    // is filtered server-side, so a changed filter has to come back as a fresh
    // slice rather than a re-filtered copy of the one on screen. That also
    // collapses any expanded "Show previous" history back to the top, which is
    // the right reset when what the feed contains changes — as does Refresh.
    async fetchInitial({ commit, state }) {
      latestFetchToken += 1;
      const token = latestFetchToken;
      commit('setLoading', true);
      try {
        const {
          fresh, catch_up: catchUp, filters, upcoming,
        } = await getIBPulseFeed({
          fresh_direction: 'initial',
          catch_up_direction: 'initial',
          ...filterPayload(state),
        });
        if (token !== latestFetchToken) return;
        commit('setFilters', filters);
        commit('setFresh', fresh);
        commit('setCatchUp', catchUp);
        commit('setUpcoming', upcoming);
      } catch (error) {
        if (token === latestFetchToken) commit('setError', true);
      } finally {
        if (token === latestFetchToken) commit('setLoading', false);
      }
    },

    // Toggle one source chip (AC4.5). Session-only, so unlike the book toggle
    // there is nothing to persist — just re-read the feed under the new set.
    async toggleSource({ commit, dispatch }, source) {
      commit('toggleSource', source);
      await dispatch('fetchInitial');
    },

    // Toggle the "show all commodities" book-filter override (AC4.2). The choice
    // is persisted server-side (AC4.3) and the feed reads it from there, so it
    // is saved before the re-fetch that widens/narrows the returned set.
    async setShowAll({ commit, dispatch }, showAll) {
      try {
        const { filters } = await setIBPulseFilter({ show_all: showAll });
        commit('setFilters', filters);
      } catch (error) {
        commit('setError', true);
        return;
      }
      await dispatch('fetchInitial');
    },

    // Flip the session-only "Show dismissed" view (AC7.4). The dismissed split
    // is resolved server-side, so re-fetch the feed with the new flag.
    async setShowDismissed({ commit, dispatch }, showDismissed) {
      commit('setShowDismissed', Boolean(showDismissed));
      await dispatch('fetchInitial');
    },

    async loadPreviousFresh({ commit, state }) {
      if (!state.events.length || state.loading) return;
      const token = latestFetchToken;
      commit('setLoading', true);
      try {
        const { fresh } = await getIBPulseFeed({
          fresh_direction: 'older',
          fresh_cursor: oldestCreatedAt(state.events),
          ...filterPayload(state),
        });
        if (token !== latestFetchToken) return;
        commit('appendFresh', fresh);
      } catch (error) {
        if (token === latestFetchToken) commit('setError', true);
      } finally {
        if (token === latestFetchToken) commit('setLoading', false);
      }
    },

    async loadPreviousCatchUp({ commit, state }) {
      if (!state.catchUp.events.length || state.loading) return;
      const token = latestFetchToken;
      commit('setLoading', true);
      try {
        const cursor = state.catchUp.events[state.catchUp.events.length - 1].last_blasted_at;
        const { catch_up: catchUp } = await getIBPulseFeed({
          catch_up_direction: 'older',
          catch_up_cursor: cursor,
          ...filterPayload(state),
        });
        if (token !== latestFetchToken) return;
        commit('appendCatchUp', catchUp);
      } catch (error) {
        if (token === latestFetchToken) commit('setError', true);
      } finally {
        if (token === latestFetchToken) commit('setLoading', false);
      }
    },

    // Live head refresh (30s poll): prepend events newer than what's loaded,
    // leaving accumulated "Show previous" history intact. A collection with
    // nothing loaded yet gets an initial load instead so it can cold-start.
    async refreshHead({ commit, state }) {
      const token = latestFetchToken;
      const freshDirection = state.events.length ? 'newer' : 'initial';
      const catchDirection = state.catchUp.events.length ? 'newer' : 'initial';
      const payload = {
        fresh_direction: freshDirection,
        catch_up_direction: catchDirection,
        ...filterPayload(state),
      };
      if (freshDirection === 'newer') payload.fresh_cursor = newestCreatedAt(state.events);
      if (catchDirection === 'newer') {
        payload.catch_up_cursor = state.catchUp.events[0].last_blasted_at;
      }
      try {
        const {
          fresh, catch_up: catchUp, filters, upcoming,
        } = await getIBPulseFeed(payload);
        // A poll that was in flight when a filter changed carries the old scope;
        // the reload it raced already holds the truth, so drop it.
        if (token !== latestFetchToken) return;
        commit('setFilters', filters);
        if (fresh) commit(freshDirection === 'newer' ? 'prependFresh' : 'setFresh', fresh);
        if (catchUp) commit(catchDirection === 'newer' ? 'prependCatchUp' : 'setCatchUp', catchUp);
        if (upcoming) commit('setUpcoming', upcoming);
      } catch (error) {
        // A failed poll keeps whatever is already on screen.
      }
    },

    // Engagement counters are best-effort aggregates — the UI action (hide,
    // blast composed) must never be blocked or rolled back by a failed report.
    async recordEngagement(context, payload) {
      try {
        await recordIBPulseEngagement(payload);
      } catch (error) {
        // counters are lossy by design
      }
    },

    // Records that the advisor opened one event, for the compliance audit log
    // (Story 9, AC9.2). Fired from opening the detail view — a deliberate read —
    // rather than from a row rendering, which the 30s poll would repeat endlessly.
    // No local dedupe: the log is append-only, and coming back to an event twice
    // is a fact an audit wants, not noise to collapse. Failures stay quiet here
    // because the record is the server's job to keep; the advisor is not the one
    // who should be interrupted over it.
    async recordView(context, eventId) {
      try {
        await recordIBPulseView(eventId);
      } catch (error) {
        // reading the event must not be blocked by a failed record
      }
    },

    // Dismiss (AC7.1/7.2): hide immediately, then persist. The server owns the
    // durable per-advisor state and bumps the aggregate dismiss counter, so a
    // failed call just leaves the optimistic hide to reconcile on the next load.
    async dismissEvent({ commit }, eventId) {
      commit('dismissEvent', eventId);
      try {
        await setIBPulseDismissed({ event_id: eventId, dismissed: true });
      } catch (error) {
        // best-effort; the optimistic hide stands until a reload reconciles
      }
    },

    // Restore a dismissed event (AC7.4, "changed their mind").
    async restoreEvent({ commit }, eventId) {
      commit('restoreEvent', eventId);
      try {
        await setIBPulseDismissed({ event_id: eventId, dismissed: false });
      } catch (error) {
        // best-effort
      }
    },

    // Minimize / expand every announcement card at once. Applied optimistically
    // (the strip must react immediately) then persisted; the server's answer is
    // the truth, and a failed save just leaves the local state to reconcile on
    // the next load.
    async setUpcomingCollapsed({ commit }, collapsed) {
      commit('setUpcomingCollapsed', collapsed);
      try {
        const saved = await setIBPulseUpcomingCollapsed({ collapsed: Boolean(collapsed) });
        commit('setUpcomingCollapsed', saved.collapsed);
      } catch (error) {
        // best-effort; the optimistic state stands until a reload reconciles
      }
    },

    // Close one announcement card. Scoped to the announcement, so the release
    // itself still lands in the feed when it publishes.
    async dismissUpcomingEvent({ commit }, eventId) {
      commit('dismissUpcoming', eventId);
      try {
        await setIBPulseDismissed({
          event_id: eventId, dismissed: true, scope: UPCOMING_SCOPE,
        });
      } catch (error) {
        // best-effort; the optimistic hide stands until a reload reconciles
      }
    },

    // Bring a closed card back (the "Show dismissed" view, AC7.4).
    async restoreUpcomingEvent({ commit }, eventId) {
      commit('restoreUpcoming', eventId);
      try {
        await setIBPulseDismissed({
          event_id: eventId, dismissed: false, scope: UPCOMING_SCOPE,
        });
      } catch (error) {
        // best-effort
      }
    },
  },
};
